// src/pages/markets/board/api/scoreIndex.ts
//
// One score is one (market_id, group) row. Nothing here merges across markets
// or groups — the lookups only ever hand back a single real row.

import type {
    BoardScore,
    EventConvictionIndex,
    ScreenerEvent,
} from './boardTypes';

export type ScoreMap = Map<string, BoardScore[]>;

function scoreKey(s: BoardScore): string {
    return `${s.market_id}|${s.group}`;
}

export function groupsWithScore(scores: BoardScore[]): Set<string> {
    return new Set(scores.map(s => s.group));
}

export function marketsForGroup(scores: BoardScore[], group: string): string[] {
    return scores.filter(s => s.group === group).map(s => s.market_id);
}

export function findScore(
    scores: BoardScore[],
    group: string,
    marketId: string | null,
): BoardScore | null {
    if (!marketId) return null;
    return scores.find(s => s.group === group && s.market_id === marketId) ?? null;
}

// Start position for the card: the index's argmax, falling back to the first
// row this group actually has.
export function primaryMarketFor(
    index: EventConvictionIndex | undefined,
    scores: BoardScore[],
    group: string,
): string | null {
    const fromIndex = index?.primary_market_by_group?.[group];
    if (fromIndex) return fromIndex;
    const first = scores.find(s => s.group === group);
    return first?.market_id ?? null;
}

export function hasConflict(index: EventConvictionIndex | undefined, group: string): boolean {
    const entry = index?.by_group?.find(g => g.group === group);
    return Boolean(entry && entry.yes_count > 0 && entry.no_count > 0);
}
// One market and no ladder — the card body that carries a price line. Mirrors
// MarketBlock's own dispatch, which only branches to a ladder above one market.
export function isSingleMarketEvent(event: ScreenerEvent): boolean {
    return event.markets.length === 1;
}
